import { Timestamp } from '@bufbuild/protobuf'
import { useMutation } from '@connectrpc/connect-query'
import { AttendanceType } from '@fingertip/creator-proto/gen/fingertip/common/enum/v1/attendance_type_pb'
import {
  createStripeBookingPaymentIntentPublic,
  updateStripeBookingPaymentIntentPublic,
} from '@fingertip/creator-proto/gen/fingertip/creator/store_payment/v1/store_payment-StorePaymentService_connectquery'
import { useCallback, useContext, useMemo } from 'react'
import { toast } from 'sonner'

import { PaymentDetailsFormSchema } from '@/components/checkout/types'
import { useToken } from '@/lib/hooks/use-token'
import { clientGetUrl } from '@/lib/utils/client-get-url'

import { RootStoreContext } from '../stores/root-store'

type Props = {
  siteSlug: string
  domain?: string
}

export const useBookingCheckout = ({ siteSlug }: Props) => {
  const { callOptions } = useToken()
  const {
    bookingCheckoutStore: {
      setClientSecret,
      setPaymentIntentId,
      clientSecret,
      paymentIntentId,
      targetBookingId,
      setTargetBookingId,
      setStage,
      reset,
    },
    bookerStore: { customPrice, customLength },
  } = useContext(RootStoreContext)
  const returnUrl = useMemo(() => {
    return `${clientGetUrl()}/${siteSlug}/booking?bookingId=${targetBookingId}&stage=SUCCESS`
  }, [targetBookingId, siteSlug])

  const mutationCreateIntent = useMutation(
    createStripeBookingPaymentIntentPublic,
    {
      callOptions,
      onSuccess: (data) => {
        setTargetBookingId(data.targetBookingId)
        setClientSecret(data.clientSecret)
        setPaymentIntentId(data.paymentIntentId)
      },
      onError: (error) => {
        toast.error(error.message)
      },
    },
  )

  const mutationUpdateIntent = useMutation(
    updateStripeBookingPaymentIntentPublic,
    {
      callOptions,
      onSuccess: (_) => {
        toast.success('Updated')
      },
      onError: (error) => {
        toast.error(error.message)
      },
    },
  )

  const applyCouponOnPayment = useCallback(
    async (
      attendanceType: AttendanceType,
      code: string,
      amountInCents: number,
    ) => {
      if (!paymentIntentId) {
        return
      }

      if (attendanceType === AttendanceType.MEETING) {
        await mutationUpdateIntent.mutateAsync({
          siteSlug,
          couponCode: code,
          paymentIntentId,
          bookingId: targetBookingId,
          amountInCents,
        })
      } else if (attendanceType === AttendanceType.GROUP) {
        await mutationUpdateIntent.mutateAsync({
          siteSlug,
          couponCode: code,
          paymentIntentId,
          attendanceId: targetBookingId,
          amountInCents,
        })
      }
    },
    [mutationUpdateIntent, paymentIntentId, siteSlug, targetBookingId],
  )

  const verifyPurchase = useCallback(
    async (
      email: string,
      startTime: Timestamp,
      eventTypes: { [key: string]: number },
      groupClassId?: string,
    ) => {
      // If a paymentId already exists, this means we are reusing a previous intent
      if (!paymentIntentId && !clientSecret) {
        await mutationCreateIntent.mutateAsync({
          siteSlug,
          eventTypes,
          groupClassId,
          email,
          agreedStartTime: startTime,
          customLength: customLength ?? undefined,
          customPrice: customPrice ?? undefined,
          // TODO: add formResponseIds to the payment intent
          // formResponseIds: formResponseIds.length > 0 ? formResponseIds : undefined,
        })
      }

      setStage('PAYMENT')
    },
    [
      clientSecret,
      customLength,
      customPrice,
      mutationCreateIntent,
      paymentIntentId,
      setStage,
      siteSlug,
    ],
  )

  const onPaymentConfirmed = useCallback(
    async ({}: { input: PaymentDetailsFormSchema }) => {
      setStage('CONFIRMED')

      setTimeout(async () => {
        reset()
      }, 5000)
    },
    [setStage, reset],
  )

  const getPaymentParams = useCallback(() => {
    return {
      clientSecret,
      returnUrl,
    }
  }, [clientSecret, returnUrl])

  return {
    getPaymentParams,
    returnUrl,
    verifyPurchase,
    onPaymentConfirmed,
    applyCouponOnPayment,
  }
}
